import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  passwordStrengthMessageId,
  PasswordStrengthMeter,
} from '../../../../../components/PasswordStrengthMeter';
import FieldGroup from '../../../../../components/FieldGroup';
import FormError from '../../../../../components/FormError';
import FormTextField from '../../../../../components/FormTextField';
import PasswordField from '../../../../../components/PasswordField';
import TallButton from '../../../../../components/TallButton';
import { RedirectType, WelcomeFieldError } from '../../../../../types';

import {
  PasswordFieldGroup,
  PrivacyPolicyContainer,
  StyledForm,
  WelcomeButtonWrapper,
} from './styled';
import messages from './WelcomeForm.messages';
import { EmailVerified, WelcomeFormErrors } from './components';
import { WelcomeMarketingConsent } from '../WelcomeMarketingConsent';
import { WelcomeFormData } from '../../hooks';
import { WelcomeRecaptcha } from '../WelcomeRecaptcha';
import { useTranslateFormErrorIfAvailable } from '../../../FormValidation.messages';

export type WelcomeFormProps = {
  displayName?: string;
  email: string;
  fieldErrors: WelcomeFieldError;
  formError: string | null;
  isLoading: boolean;
  onSubmit: (data: WelcomeFormData) => Promise<void>;
  onWelcomeClick?: () => void;
  privacyPolicy: React.ReactNode | undefined;
  redirectType: RedirectType;
};

export const WelcomeForm = ({
  displayName,
  email,
  fieldErrors,
  formError,
  isLoading,
  onSubmit,
  onWelcomeClick,
  privacyPolicy,
  redirectType,
}: WelcomeFormProps) => {
  const intl = useIntl();
  const [fullName, setFullName] = useState(() => displayName || '');
  const [password, setPassword] = useState('');

  const displayNameInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const fieldErrorPassword = useTranslateFormErrorIfAvailable(fieldErrors.password);
  const fieldErrorDisplayName = useTranslateFormErrorIfAvailable(fieldErrors.displayName);

  useEffect(() => {
    const { displayName, emailUsedAsName, passwordUsedAsName } = fieldErrors;

    const displayNameError = displayName || emailUsedAsName || passwordUsedAsName;
    const passwordError = fieldErrors.password;

    if (displayNameError) {
      displayNameInputRef.current?.focus();
    } else if (passwordError) {
      passwordInputRef.current?.focus();
    }
  }, [fieldErrors]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isLoading) {
      return;
    }

    await onSubmit({
      displayName: fullName,
      password,
    });
  };

  return (
    <div>
      {formError && (
        <FormError>
          <WelcomeFormErrors error={formError} />
        </FormError>
      )}
      <StyledForm onSubmit={handleSubmit}>
        {({ formProps }) => (
          <form {...formProps} id="form-sign-up" onSubmit={handleSubmit}>
            <EmailVerified
              email={email}
              redirectType={redirectType}
              iconLabel={intl.formatMessage(messages.emailAddressVerifiedLabel)}
            />
            <FieldGroup>
              <FormTextField
                testId="displayName" // used in pollinators
                name="displayName"
                isRequired
                autoComplete={'name'}
                placeholder={intl.formatMessage(messages.displayNamePlaceholder)}
                label={intl.formatMessage(messages.fullNameLabel)}
                autoFocus={!!email && !displayName}
                isLabelHidden={false}
                isInvalid={
                  !!fieldErrorDisplayName ||
                  !!fieldErrors.emailUsedAsName ||
                  !!fieldErrors.passwordUsedAsName
                }
                invalidMessage={
                  fieldErrorDisplayName ||
                  fieldErrors.emailUsedAsName ||
                  fieldErrors.passwordUsedAsName
                }
                onChange={e => setFullName(e.currentTarget.value)}
                value={fullName}
                ref={displayNameInputRef}
              />
            </FieldGroup>
            <PasswordFieldGroup>
              <PasswordField
                testId="password" // used in pollinators
                name="password"
                isRequired
                autoComplete="new-password"
                aria-describedby={passwordStrengthMessageId}
                placeholder={intl.formatMessage(messages.passwordPlaceholder)}
                label={intl.formatMessage(messages.passwordLabelNew)}
                autoFocus={!!email && !!displayName}
                isLabelHidden={false}
                isInvalid={!!fieldErrorPassword}
                invalidMessage={fieldErrorPassword}
                onChange={e => setPassword(e.currentTarget.value)}
                value={password}
                ref={passwordInputRef}
              />
              <PasswordStrengthMeter
                password={password}
                email={email}
                displayName={fullName}
                data-testid="password-strength-meter"
              />
            </PasswordFieldGroup>
            <WelcomeMarketingConsent />
            <PrivacyPolicyContainer>{privacyPolicy}</PrivacyPolicyContainer>
            <WelcomeRecaptcha />
            <WelcomeButtonWrapper>
              <TallButton
                appearance="primary"
                type="submit"
                isFullWidth
                id="signup-submit"
                isLoading={isLoading}
                onClick={onWelcomeClick}>
                <FormattedMessage id="welcome.form.submit.continue" defaultMessage="Continue" />
              </TallButton>
            </WelcomeButtonWrapper>
          </form>
        )}
      </StyledForm>
    </div>
  );
};
